import React, { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
  Icon,
  showToast,
  Card,
  Skeleton,
} from '@repo/ui';
import { reportingEmailQueueApi } from '../../api';
import { EmailPreviewEditor } from './EmailPreviewEditor';
import { CcInput } from './CcInput';
import { ArEmailQueueMatter, ArEmailSend } from '../../types/emailQueue';
import { SubjectInput } from './SubjectInput';
import { ToAddressInput } from './ToAddressInput';
import { useArTemplate, usePreviewFileQuery } from '../../queries';
import { useArEmailCollectionsStore, useQueryKeyStore } from '@xtnd/web/shared/stores';
import { getRowUniqueKey, populateEmailMessage, createEmailQueueUpdater } from './utils';
import { ArEmailGroupByOption } from '@repo/types';
import { ArMatterResponse } from '../../types';
import { AttachmentInput, AttachmentItem } from './AttachmentInput';
interface IPreviewEmail {
  open: boolean;
  onClose: () => void;
  email: ArEmailQueueMatter | null;
  groupBy: ArEmailGroupByOption | string;
  daysPastDue?: number;
  daysPastDueCap?: number;
}

export const PreviewEmailModal = React.memo(
  ({ open, onClose, email, groupBy, daysPastDue, daysPastDueCap }: IPreviewEmail) => {
  const { isSending, selectedKeys, updateSelectedKeys, updateMatters, setIsSending } =
    useArEmailCollectionsStore();
  const { getQueryKey } = useQueryKeyStore();
  const [formData, setFormData] = useState<ArEmailSend | null>(null);
  const queryKey = getQueryKey('ar-collections', 'reporting.email-queue');
  const matterKey = getQueryKey('ar-collections', 'queue.matters');
  const queryClient = useQueryClient();
  const {
    data: template,
    isLoading,
    error: isFetchTemplateError,
  } = useArTemplate(email?.matterId || '', email?.clientId || '', {
    enabled: !!email?.matterId,
    daysPastDue,
    daysPastDueCap,
  });

  const [previewFileKey, setPreviewFileKey] = useState<string | null>(null);
  const [newBucket, setNewBucket] = useState<string>('');

  const { isLoading: isDocLoading, data: document } = usePreviewFileQuery(
    previewFileKey || '',
    undefined,
    newBucket
  );

  useEffect(() => {
    if (previewFileKey && document?.data) {
      window.open(document.data, '_blank');
      setPreviewFileKey(null);
      setNewBucket('');
    }
  }, [document, previewFileKey, newBucket]);

  // Reset form on open / different matter (email identity change). Intentionally
  // does NOT depend on `template` — when the user changes the aging-bucket
  // filter mid-preview the template ref changes (new fetch / different bound
  // bucket scoped invoices), and we'd otherwise clobber any in-progress edits
  // to recipients/subject/body. Trade-off: if the bucket changes while preview
  // is open the new template fields don't auto-populate; user can close and
  // re-open to refresh.
  useEffect(() => {
    if (open && email && template?.data) {
      setFormData({
        matterId: email?.matterId,
        clientId: email?.clientId,
        invoiceNumbers: email?.invoiceNumbers || [],
        toAddresses: template?.data.toAddresses || [],
        ccAddresses: template?.data.ccAddresses,
        subject: template?.data.subject || '',
        message: template?.data.message || '',
        attachments: (template?.data.attachments || []).map(att => ({
          ...att,
          checked: (att as AttachmentItem).checked !== false, // Default to true if not explicitly false
        })) as AttachmentItem[],
      });
    } else {
      setFormData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, email]);

  // Seed formData when the template arrives AFTER the modal opens (initial
  // async load). Only populates when formData hasn't been initialized yet, so
  // this never overwrites user edits.
  useEffect(() => {
    if (open && email && !formData && template?.data) {
      setFormData({
        matterId: email?.matterId,
        clientId: email?.clientId,
        invoiceNumbers: email?.invoiceNumbers || [],
        toAddresses: template?.data.toAddresses || [],
        ccAddresses: template?.data.ccAddresses,
        subject: template?.data.subject || '',
        message: template?.data.message || '',
        attachments: (template?.data.attachments || []).map(att => ({
          ...att,
          checked: (att as AttachmentItem).checked !== false,
        })) as AttachmentItem[],
      });
    }
  }, [open, email, formData, template]);

  const handleSendEmail = async () => {
    if (!formData) return;
    if ((formData?.toAddresses ?? []).length === 0) {
      showToast.warning(`Email cannot be sent without a recipient.`);
      return;
    }
    setIsSending(true);
    if (formData) {
      // Filter to only include checked attachments
      const emailData = {
        ...formData,
        attachments: (formData.attachments || []).filter(
          (att: AttachmentItem) => att.checked !== false
        ),
      };

      try {
        await reportingEmailQueueApi.sendArEmail({ items: [emailData] });
        if (email) {
          updateMatters(email);
          if (selectedKeys.length > 0) {
            updateSelectedKeys(getRowUniqueKey(email));
          }
        }
        //Update React Query cache
        queryClient.setQueryData(
          queryKey,
          createEmailQueueUpdater(email ?? undefined, groupBy as ArEmailGroupByOption)
        );
        // remove from collections queue
        queryClient.setQueryData(matterKey, (oldData: ArMatterResponse) => {
          if (!oldData?.data) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              canSendEmail: false,
            },
          };
        });

        populateEmailMessage({
          error: null,
          email: emailData?.toAddresses?.join(' ,') ?? null,
        });
        onClose();
      } catch (error) {
        populateEmailMessage({
          error: error,
          email: emailData?.toAddresses?.join(' ,') ?? null,
        });
      } finally {
        setIsSending(false);
      }
    }
  };

  const handleAddAttachment = (files: File[]) => {
    const newAttachments: AttachmentItem[] = files.map((file, index) => ({
      filename: file.name,
      fileKey: `${Date.now()}-${index}-${file.name}`,
      fileSize: file.size,
      file,
      checked: true,
    }));

    setFormData(prev =>
      prev ? { ...prev, attachments: [...(prev.attachments || []), ...newAttachments] } : null
    );
  };

  const handleViewAttachment = (item: AttachmentItem) => {
    if (item.file) {
      window.open(URL.createObjectURL(item.file), '_blank');
      return;
    }
    setPreviewFileKey(item.fileKey);
    setNewBucket(item?.category ?? '');
  };

  const renderEditor = useCallback(() => {
    const sharedButtons = [
      'bold',
      'italic',
      'underline',
      'brush',
      '|',
      'link',
      'unlink',
      '|',
      'undo',
      'redo',
      '|',
      'preview',
    ];

    const config = {
      readonly: false,
      iframe: false,
      toolbarSticky: true,
      showXPathInStatusbar: false,
      showCharsCounter: false,
      showWordsCounter: false,
      toolbarAdaptive: false,
      height: 'auto',
      minHeight: 200,
      maxHeight: -1,
      autoresize: true,
      buttons: sharedButtons,
      buttonsMD: sharedButtons,
      buttonsSM: sharedButtons,
      buttonsXS: sharedButtons,
      removeButtons: ['source', 'fullsize', 'about', 'spellcheck', 'hr', 'image', 'video', 'table'],
      style: {
        overflow: 'auto',
      },
      // allow copy and pass text
      allowPaste: true,
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      disablePlugins: ['pasteStorage'],
      beautifyHTML: false,
      events: {
        beforePaste: () => true,
        paste: () => true,
      },
    };
    return (
      <EmailPreviewEditor
        config={config}
        value={formData ? formData?.message : ''}
        onChange={value => {
          setFormData(prev =>
            prev
              ? {
                ...prev,
                message: value,
              }
              : prev
          );
        }}
      />
    );
  }, [formData]);

  const renderErrorMessage = useCallback(() => {
    if (isFetchTemplateError) {
      const message = isFetchTemplateError.message.split('-');
      const [, maxDaysPastDue, clientType] = message;
      return (
        <div className="h-full flex items-center justify-center">
          <div className="max-w-md flex flex-col items-center gap-3 text-center p-6 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
            <Icon name="HiOutlineExclamationCircle" className="h-6 w-6 text-red-500" />
            <p className="text-base font-semibold text-gray-800 dark:text-gray-100">
              Unable to Load Email Template
            </p>

            <p className="text-sm text-gray-600 dark:text-gray-300">
              This matter belongs to a{' '}
              <span className="font-medium text-gray-800 dark:text-gray-100">
                {clientType || 'unknown'}
              </span>{' '}
              client.
            </p>

            {maxDaysPastDue && (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                The maximum days past due among the invoices is{' '}
                <span className="font-medium text-gray-800 dark:text-gray-100">
                  {maxDaysPastDue} days
                </span>
                , which does not meet the criteria to send an email.
              </p>
            )}
          </div>
        </div>
      );
    }
    return <></>;
  }, [isFetchTemplateError]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="xxllg"
      className={`
      h-[95vh]
      transition-all duration-300 ease-out
      transform
      ${open ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
    `}
    >
      <div className="flex flex-col justify-between h-full ">
        {isLoading ? (
          <div className="flex flex-col justify-between">
            <Card className="mt-16 p-6 space-y-6 animate-pulse">
              <div className="space-y-2">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-10 w-full rounded-md" />
              </div>

              <div className="space-y-2">
                <Skeleton className="h-4 w-12" />
                <div className="flex flex-wrap gap-2 border dark:border-xtnd-dark rounded-md p-2">
                  <Skeleton className="h-8 w-48 rounded-full" />
                  <Skeleton className="h-8 w-56 rounded-full" />
                  <Skeleton className="h-8 w-40 rounded-full" />
                  <Skeleton className="h-8 w-32 rounded-full" />
                </div>
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-10 w-full rounded-md" />
              </div>

              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <div className="flex gap-3 flex-wrap">
                  {[...Array(8)].map((_, i) => (
                    <Skeleton key={i} className="h-14 w-40 rounded-md" />
                  ))}
                  <Skeleton className="h-14 w-14 rounded-md" />
                </div>
              </div>
              <div className="space-y-3">
                <Skeleton className="h-8 w-full rounded-md" />
                <Skeleton className="h-48 w-full rounded-md" />
              </div>
            </Card>
            <div className="flex justify-end gap-4 pt-4">
              <Skeleton className="min-h-8 max-w-24 rounded-md" />
              <Skeleton className="min-h-8 max-w-36 rounded-md" />
            </div>
          </div>
        ) : (
          <>
            {isFetchTemplateError ? (
              renderErrorMessage()
            ) : (
              <>
                <DialogHeader className="h-15">
                  <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
                    Email Preview - {email?.matterDisplayNumber}
                  </h2>
                </DialogHeader>
                <DialogBody className="!py-2 !bg-transparent overflow-y-auto">
                  <div className="text-black dark:bg-xtnd-dark-800 rounded-lg flex flex-col gap-4 h-full px-2">
                    <div className="flex flex-col gap-3">
                      <ToAddressInput
                        value={formData?.toAddresses?.join(',') || ''}
                        onChange={(email: string) => {
                          setFormData(prev =>
                            prev
                              ? {
                                ...prev,
                                toAddresses: email.split(',').map(e => e.trim()),
                              }
                              : null
                          );
                        }}
                      />
                      <CcInput
                        value={formData?.ccAddresses || []}
                        onChange={(emails: string[]) => {
                          const filteredEmails = emails?.filter(e => e.trim() !== '');
                          setFormData(prev =>
                            prev
                              ? {
                                ...prev,
                                ccAddresses: filteredEmails,
                              }
                              : prev
                          );
                        }}
                      />
                      <SubjectInput
                        value={formData?.subject || ''}
                        onChange={val =>
                          setFormData(prev => (prev ? { ...prev, subject: val } : null))
                        }
                      />
                      <AttachmentInput
                        items={formData?.attachments || []}
                        onView={handleViewAttachment}
                            onToggleChecked={(item, checked) => {
                          setFormData(prev =>
                            prev
                              ? {
                                ...prev,
                                attachments: prev.attachments?.map(att =>
                                  att.fileKey === item.fileKey
                                    ? { ...att, checked }
                                    : att
                                ),
                              }
                              : null
                          );
                        }}
                        onAdd={handleAddAttachment}
                        loadingFileKey={isDocLoading ? previewFileKey : null}
                      />
                    </div>
                    <div className="border rounded-lg flex flex-col p-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1 block ">
                        Message
                      </span>
                      {renderEditor()}
                    </div>
                  </div>
                </DialogBody>
                <DialogFooter>
                  <Button variant="outline" onClick={onClose} disabled={false}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    leftIcon={<Icon name="FiSend" />}
                    onClick={handleSendEmail}
                    loading={isSending}
                  >
                    {isSending ? 'Sending...' : 'Send Email'}
                  </Button>
                </DialogFooter>
              </>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
});

PreviewEmailModal.displayName = 'PreviewEmailModal';
